import { useState, useEffect } from 'react'
import { Phone, Bot, Clock, Mic, Pencil, Save, X, ChevronDown, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'
import { formatPhoneDisplay } from '@/lib/utils'

interface OrgVoiceConfig {
  organization: {
    id: string
    name: string
    voiceEnabled: boolean
    callDirection: string | null
    limits: {
      monthlyVoiceMinutes: number
      phoneNumbers: number
    }
  }
  phoneNumbers: any[]
  assistants: any[]
  recentCalls: any[]
}

interface AssistantsNumbersSectionProps {
  orgId: string
}

export default function AssistantsNumbersSection({ orgId }: AssistantsNumbersSectionProps) {
  const { toast } = useToast()
  const [voiceConfig, setVoiceConfig] = useState<OrgVoiceConfig | null>(null)
  const [editingAssistant, setEditingAssistant] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ name: '', firstMessage: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    loadVoiceConfig()
  }, [orgId])

  const loadVoiceConfig = async () => {
    try {
      const response = await api.get(`/api/platform/organizations/${orgId}/voice`)
      if (response.data.success) {
        setVoiceConfig(response.data)
      }
    } catch (error) {
      console.error('Failed to load voice config:', error)
    }
  }

  const formatPhone = (phone: string | null) => formatPhoneDisplay(phone)
  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`
  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default', online: 'default', pending: 'secondary',
      offline: 'secondary', suspended: 'destructive', completed: 'default',
      in_progress: 'secondary', missed: 'destructive'
    }
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>
  }

  if (!voiceConfig) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <RefreshCw className="h-5 w-5 mx-auto mb-2 animate-spin opacity-40" />
          Loading assistants & numbers...
        </CardContent>
      </Card>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Phone className="h-4 w-4" />
                Assistants & Numbers
                <Badge variant="secondary" className="text-xs ml-1">
                  {voiceConfig.assistants.length} agents · {voiceConfig.phoneNumbers.length} numbers
                </Badge>
              </CardTitle>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            <CardDescription>Phone numbers, AI assistants, and recent calls</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            {/* Stat Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xl font-bold">{voiceConfig.phoneNumbers.length}</span>
                </div>
                <p className="text-xs text-muted-foreground">Phone Numbers</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xl font-bold">{voiceConfig.assistants.length}</span>
                </div>
                <p className="text-xs text-muted-foreground">AI Assistants</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xl font-bold">{voiceConfig.organization.limits.monthlyVoiceMinutes}</span>
                </div>
                <p className="text-xs text-muted-foreground">Monthly Minutes</p>
              </div>
            </div>

            {/* Phone Numbers */}
            {voiceConfig.phoneNumbers.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Phone Numbers</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Provider</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {voiceConfig.phoneNumbers.map((phone: any) => (
                      <TableRow key={phone.id}>
                        <TableCell className="font-mono text-sm">{formatPhone(phone.phoneNumber)}</TableCell>
                        <TableCell>{getStatusBadge(phone.status)}</TableCell>
                        <TableCell className="text-sm">{phone.provider}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Assistants */}
            {voiceConfig.assistants.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">AI Assistants</h4>
                <div className="space-y-3">
                  {voiceConfig.assistants.map((asst: any) => {
                    const isEditing = editingAssistant === asst.id
                    return (
                      <div key={asst.id} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Mic className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium text-sm">{asst.name}</span>
                            <Badge variant="outline" className="text-xs">{asst.assistantType || 'intake'}</Badge>
                            {asst.metadata?.kbUploadStatus === 'success' && (
                              <Badge variant="default" className="text-[10px] bg-green-100 text-green-700 border-green-200">KB Ready</Badge>
                            )}
                            {asst.metadata?.kbUploadStatus === 'failed' && (
                              <Badge variant="destructive" className="text-[10px]">KB Failed</Badge>
                            )}
                            {asst.metadata?.kbUploadStatus === 'pending' && (
                              <Badge variant="secondary" className="text-[10px]">KB Uploading...</Badge>
                            )}
                          </div>
                          {!isEditing ? (
                            <Button variant="outline" size="sm" onClick={() => {
                              setEditingAssistant(asst.id)
                              setEditForm({ name: asst.name || '', firstMessage: asst.firstMessage || '' })
                            }}>
                              <Pencil className="h-3 w-3 mr-1" /> Edit
                            </Button>
                          ) : (
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => setEditingAssistant(null)}>
                                <X className="h-3 w-3 mr-1" /> Cancel
                              </Button>
                              <Button size="sm" disabled={isSaving} onClick={async () => {
                                setIsSaving(true)
                                try {
                                  await api.put(`/api/platform/organizations/${orgId}/voice/assistants/${asst.id}`, editForm)
                                  toast({ title: 'Assistant updated', description: 'Changes synced to voice provider.' })
                                  setEditingAssistant(null)
                                  loadVoiceConfig()
                                } catch {
                                  toast({ title: 'Error', description: 'Failed to update.', variant: 'destructive' })
                                } finally {
                                  setIsSaving(false)
                                }
                              }}>
                                <Save className="h-3 w-3 mr-1" /> {isSaving ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <div>
                              <Label className="text-xs text-muted-foreground">Name</Label>
                              <Input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Assistant name" className="h-9" />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">First Message</Label>
                              <Textarea value={editForm.firstMessage} onChange={(e) => setEditForm(f => ({ ...f, firstMessage: e.target.value }))}
                                rows={2} placeholder="The first thing the AI says..." />
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">{asst.firstMessage || '(no first message set)'}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Recent Calls */}
            {voiceConfig.recentCalls.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Recent Calls</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Direction</TableHead>
                      <TableHead>From/To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {voiceConfig.recentCalls.map((call: any) => (
                      <TableRow key={call.id}>
                        <TableCell><Badge variant="outline" className="text-xs">{call.direction}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">
                          {call.direction === 'inbound' ? formatPhone(call.phoneNumberFrom) : formatPhone(call.phoneNumberTo)}
                        </TableCell>
                        <TableCell>{getStatusBadge(call.status)}</TableCell>
                        <TableCell className="text-sm">
                          {call.durationSeconds ? `${Math.floor(call.durationSeconds / 60)}m ${call.durationSeconds % 60}s` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {call.cost != null ? formatCurrency(call.cost) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
